import api from './api';

// Definir interfaz para usuario
export interface Usuario {
  id?: number;
  nom_usuario?: string;
  nombre: string;
  apellido?: string;
  email: string;
  pass?: string;
  estado?: number;
  estado_pass?: number;
  tabla_grupo?: string;
  id_grupo?: number;
  notificaciones?: number;
  passwordPendiente?: boolean;
  tipo_usuario?: 'cliente' | 'laboratorio' | 'administrador';
  p_busqueda?: boolean;
  p_carga?: boolean;
  p_edicion?: boolean;
  p_eliminar?: boolean;
  p_admin?: boolean;
  p_modif_analisis?: boolean | number;
  p_nuevo_analisis?: boolean | number;
  p_pendientes?: boolean | number;
  p_g_alertas?: boolean | number;
  p_g_config?: boolean | number;
  p_g_ensayos?: boolean | number;
  p_g_usuarios?: boolean | number;
}

// Interfaz para usuarios agrupados por planta
export interface UsuariosPorPlanta {
  planta: {
    id: number;
    id_cliente: number;
    denominacion: string;
    direccion: string;
  };
  usuarios: Usuario[];
}

// Interfaz para usuarios agrupados por cliente
export interface UsuariosPorCliente {
  cliente: {
    id: number;
    nombre: string;
    razon_social: string;
    cuit: string;
    telefono: string;
    direccion: string;
    ciudad: string;
    provincia: string;
    email: string;
    estado: number; // 0 = activo, 1 = inactivo
    p_busqueda?: boolean;
    p_carga?: boolean;
    p_modif_analisis?: boolean;
    p_nuevo_analisis?: boolean;
    p_pendientes?: number;
  };
  usuarios: Usuario[];
}

// Interfaz para clientes con plantas asociadas
export interface ClienteConPlantas {
  cliente: {
    id: number;
    nombre: string;
    razon_social: string;
    cuit: string;
    telefono: string;
    direccion: string;
    ciudad: string;
    provincia: string;
    email: string;
    estado: number; // 0 = activo, 1 = inactivo
    p_busqueda?: boolean;
    p_carga?: boolean;
    p_modif_analisis?: boolean;
    p_nuevo_analisis?: boolean;
    p_pendientes?: number;
  };
  plantas: {
    id: number;
    id_cliente: number;
    denominacion: string;
    direccion: string;
    ciudad?: string;
    activo?: number;
  }[];
}

// Interfaz para laboratorios con usuarios asociados
export interface LaboratorioConUsuarios {
  laboratorio: {
    id: number;
    nombre: string;
    cuit: string;
    telefono: string;
    direccion: string;
    email: string;
    estado: number; // 0 = activo, 1 = inactivo
    p_busqueda?: boolean;
    p_carga?: boolean;
    p_modif_analisis?: boolean;
    p_nuevo_analisis?: boolean;
    p_pendientes?: number;
  };
  usuarios: Usuario[];
}

// Obtener todos los usuarios
export const getUsuarios = async (): Promise<Usuario[]> => {
  const response = await api.get('/usuarios');
  return response.data;
};

// Obtener usuarios agrupados por plantas
export const getUsuariosByPlantas = async (): Promise<UsuariosPorPlanta[]> => {
  const response = await api.get('/usuarios/by-plantas');
  return response.data;
};

// Obtener usuarios agrupados por clientes
export const getUsuariosByClientes = async (): Promise<UsuariosPorCliente[]> => {
  const response = await api.get('/usuarios/by-clientes');
  return response.data;
};

// Crear un nuevo usuario
export const createUsuario = async (usuario: Usuario): Promise<Usuario> => {
  const response = await api.post('/usuarios', usuario);
  return response.data;
};

// Actualizar un usuario
export const updateUsuario = async (id: number, usuario: Partial<Usuario>): Promise<Usuario> => {
  const response = await api.put(`/usuarios/${id}`, usuario);
  return response.data;
};

// Suspensión temporal de una cuenta: estado 0. Sigue visible en la lista y se
// reactiva cuando haga falta.
export const desactivarUsuario = async (id: number): Promise<Usuario> =>
  updateUsuario(id, { estado: 0 });

// Eliminación de una cuenta: el registro no se borra, pasa a estado 2. Deja de
// aparecer en la aplicación, pero los análisis que generó o finalizó conservan
// la referencia a su autor.
export const eliminarUsuario = async (id: number): Promise<void> => {
  await api.delete(`/usuarios/${id}`);
};

// Eliminación del usuario junto con su organización (cliente o laboratorio).
// Ambos pasan a estado 2, sin perder el historial asociado.
export const eliminarUsuarioCompleto = async (id: number): Promise<void> => {
  await api.delete(`/usuarios/completo/${id}`);
};

// Reactiva una cuenta dada de baja.
export const activarUsuario = async (id: number): Promise<Usuario> =>
  updateUsuario(id, { estado: 1 });

export interface ResultadoClaveTemporal {
  emailSent: boolean;
  // Solo llega cuando el correo no pudo enviarse, para entregarla a mano.
  generatedPass?: string | null;
  message?: string;
}

export const resetTemporaryPassword = async (id: number): Promise<ResultadoClaveTemporal> => {
  const response = await api.post(`/usuarios/${id}/reset-password`);
  return response.data;
};

// Obtener clientes con sus plantas asociadas
export const getClientesConPlantas = async (): Promise<ClienteConPlantas[]> => {
  const response = await api.get('/usuarios/clientes-con-plantas');
  return response.data;
};

// Obtener laboratorios con sus usuarios asociados
export const getLaboratoriosConUsuarios = async (): Promise<LaboratorioConUsuarios[]> => {
  const response = await api.get('/usuarios/laboratorios-con-usuarios');
  return response.data;
};

// Actualizar perfil propio (nombre, apellido, email)
export const updateMyProfile = async (data: { nombre?: string; apellido?: string; email?: string }) => {
  const response = await api.put('/usuarios/me/profile', data);
  return response.data;
};

// Cambiar contraseña propia (requiere estado_pass = 0)
export const changeMyPassword = async (payload: { oldPassword: string; newPassword: string }) => {
  const response = await api.post('/usuarios/me/password', payload);
  return response.data;
};
